import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { DollarSign, Users, Car, TrendingUp, MessageCircle } from "lucide-react";
import { useUnreadMessages } from "@/lib/useUnreadMessages";
import PhotoEditor from "@/components/admin/PhotoEditor";
import TotpSetup from "@/components/admin/TotpSetup";
import VerifyQueue from "@/components/admin/VerifyQueue";

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [block, setBlock] = useState({ vehicle_id: "", start_date: "", end_date: "", reason: "Maintenance" });
  const { count: unread } = useUnreadMessages();

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { nav("/login"); return; }
    Promise.all([
      api.get("/admin/analytics"),
      api.get("/admin/bookings"),
      api.get("/vehicles"),
    ]).then(([a, b, v]) => {
      setAnalytics(a.data); setBookings(b.data); setVehicles(v.data);
      setBlock((prev) => ({ ...prev, vehicle_id: v.data?.[0]?.vehicle_id || "" }));
    });
  }, [user, loading, nav]);

  const submitBlock = async () => {
    try {
      await api.post("/admin/block-dates", block);
      toast.success("Dates blocked");
    } catch (e) {
      toast.error("Failed to block dates");
    }
  };

  if (loading || !user) return <div className="container-bl py-20">Loading…</div>;
  if (user.role !== "admin") return null;

  return (
    <div className="container-bl py-10" data-testid="page-admin">
      <Seo title="Admin · B&L Rentals" />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-white">Admin dashboard</h1>
          <p className="mt-1 text-[#AEB9C9]">Manage bookings, vehicles, and view business analytics.</p>
        </div>
        <Link
          to="/messages"
          data-testid="admin-open-messages"
          className="relative inline-flex items-center gap-2 bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <MessageCircle size={16} /> Open customer messages
          {unread > 0 && (
            <span
              data-testid="unread-badge-admin"
              className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#010F27] text-[#E2AB20] text-[11px] font-bold leading-none"
            >{unread}</span>
          )}
        </Link>
      </div>

      {analytics && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: DollarSign, label: "Revenue", value: `$${analytics.revenue?.toLocaleString?.() || 0}` },
            { icon: TrendingUp, label: "Total bookings", value: analytics.total_bookings },
            { icon: Users, label: "Users", value: analytics.active_users },
            { icon: Car, label: "Vehicles", value: vehicles.length },
          ].map((k, i) => (
            <div key={i} className="card-bl p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-[#AEB9C9]">{k.label}</div>
                <k.icon size={16} className="text-[#E2AB20]" />
              </div>
              <div className="mt-2 text-2xl font-heading font-bold text-white">{k.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8">
        <Tabs defaultValue="bookings">
          <TabsList>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="admin-tab-vehicles">Vehicles</TabsTrigger>
            <TabsTrigger value="photos" data-testid="admin-tab-photos">Photos</TabsTrigger>
            <TabsTrigger value="verify" data-testid="admin-tab-verify">Verify</TabsTrigger>
            <TabsTrigger value="block" data-testid="admin-tab-block">Block dates</TabsTrigger>
            <TabsTrigger value="security" data-testid="admin-tab-security">Security</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            <div className="card-bl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">Booking</th>
                    <th className="p-3">Vehicle</th>
                    <th className="p-3">Guest</th>
                    <th className="p-3">Dates</th>
                    <th className="p-3">Total</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.booking_id} className="border-t" data-testid={`admin-booking-${b.booking_id}`}>
                      <td className="p-3 font-mono text-xs">{b.booking_id}</td>
                      <td className="p-3">{b.vehicle_name}</td>
                      <td className="p-3">{b.guest_name}<div className="text-xs text-[#AEB9C9]">{b.guest_email}</div></td>
                      <td className="p-3">{b.start_date} → {b.end_date}</td>
                      <td className="p-3">${b.total?.toFixed(2)}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${b.status === "confirmed" ? "bg-green-50 text-green-700" : "bg-gray-100 text-[#AEB9C9]"}`}>{b.status}</span></td>
                    </tr>
                  ))}
                  {bookings.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#AEB9C9]">No bookings yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card-bl p-5">
                <div className="font-heading font-semibold text-white">Monthly bookings</div>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics?.trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                      <YAxis stroke="#9ca3af" fontSize={12} />
                      <Tooltip />
                      <Line type="monotone" dataKey="bookings" stroke="#E2AB20" strokeWidth={2} dot={{ fill: "#E2AB20" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="card-bl p-5">
                <div className="font-heading font-semibold text-white">Popular vehicles</div>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics?.popular_vehicles || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} interval={0} angle={-10} textAnchor="end" height={60} />
                      <YAxis stroke="#9ca3af" fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="bookings" fill="#E2AB20" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="vehicles" className="mt-6">
            <div className="card-bl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr><th className="p-3">Vehicle</th><th className="p-3">Type</th><th className="p-3">Price/day</th><th className="p-3">Capacity</th><th className="p-3">Rating</th></tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => (
                    <tr key={v.vehicle_id} className="border-t" data-testid={`admin-vehicle-${v.slug}`}>
                      <td className="p-3">{v.name}</td>
                      <td className="p-3">{v.type}</td>
                      <td className="p-3">${v.price_per_day}</td>
                      <td className="p-3">{v.capacity}</td>
                      <td className="p-3">{v.rating} ({v.review_count})</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="photos" className="mt-6">
            <PhotoEditor
              vehicles={vehicles}
              onChange={() => api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {})}
            />
          </TabsContent>

          <TabsContent value="verify" className="mt-6">
            <VerifyQueue />
          </TabsContent>

          <TabsContent value="block" className="mt-6">
            <div className="card-bl p-6 max-w-lg">
              <h2 className="font-heading font-semibold text-lg">Block maintenance dates</h2>
              <div className="mt-4 space-y-3">
                <div>
                  <Label>Vehicle</Label>
                  <select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={block.vehicle_id} onChange={(e) => setBlock({...block, vehicle_id: e.target.value})} data-testid="block-vehicle-select">
                    {vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Start (YYYY-MM-DD)</Label><Input value={block.start_date} onChange={(e) => setBlock({...block, start_date: e.target.value})} data-testid="block-start" /></div>
                  <div><Label>End (YYYY-MM-DD)</Label><Input value={block.end_date} onChange={(e) => setBlock({...block, end_date: e.target.value})} data-testid="block-end" /></div>
                </div>
                <div><Label>Reason</Label><Input value={block.reason} onChange={(e) => setBlock({...block, reason: e.target.value})} data-testid="block-reason" /></div>
                <Button onClick={submitBlock} className="bg-[#E2AB20] hover:bg-[#D89B08]" data-testid="block-submit">Block dates</Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="security" className="mt-6">
            <TotpSetup />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
